// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file reconcileMessages.js
 * @description Fusiona historial (API) con mensajes en tiempo real (WS) sin duplicados ni overwrite.
 * Orden canónico único: fecha_envio (cadena de orden) → ms; empate → id ASC; optimistas temp_* tras reales del mismo instante.
 */

import { isArchatI18nV1BlobString } from './archatNoticePayload';

/**
 * Valor textual usado solo para ordenar (no para UI).
 * Regla: **fecha_envio** en mensaje/raw primero; **fecha_envio_iso** antes que created_at (WS/API a veces solo envían ISO);
 * created_at / timestamp solo si no hay fecha canónica útil.
 * @param {object|null|undefined} m
 * @returns {string|null}
 */
export function pickMessageSortDateRaw(m) {
  if (m == null || typeof m !== 'object') return null;
  const tryStr = (v) => {
    if (v == null) return null;
    const s = typeof v === 'string' ? v.trim() : String(v).trim();
    return s !== '' ? s : null;
  };
  let v = tryStr(m.fecha_envio);
  if (v) return v;
  v = tryStr(m.raw?.fecha_envio);
  if (v) return v;
  v = tryStr(m.fecha_envio_iso);
  if (v) return v;
  v = tryStr(m.raw?.fecha_envio_iso);
  if (v) return v;
  v = tryStr(m.created_at ?? m.createdAt);
  if (v) return v;
  v = tryStr(m.raw?.created_at);
  if (v) return v;
  v = tryStr(m.timestamp);
  if (v) return v;
  v = tryStr(m.raw?.timestamp);
  if (v) return v;
  v = tryStr(m.fecha);
  if (v) return v;
  v = tryStr(m.raw?.fecha);
  return v;
}

/**
 * Convierte la cadena de orden a ms. MySQL naive `YYYY-MM-DD HH:MM:SS` → componentes **locales**.
 * ISO con Z u offset → `Date` estándar (UTC según especificación).
 * Inválido → 0 (desempate por id; sin `new Date()` “ahora”).
 * @param {string|null|undefined} rawStr
 * @returns {number}
 */
export function parseMessageSortDateToMs(rawStr) {
  if (rawStr == null || rawStr === '') return 0;
  const s = typeof rawStr === 'string' ? rawStr.trim() : String(rawStr).trim();
  if (s === '') return 0;
  const mysqlNaive = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?$/;
  const mm = mysqlNaive.exec(s);
  if (mm) {
    const frac = mm[7] ? String(mm[7]).padEnd(3, '0').slice(0, 3) : '000';
    const msPart = Number(frac);
    const tLocal = new Date(
      Number(mm[1]),
      Number(mm[2]) - 1,
      Number(mm[3]),
      Number(mm[4]),
      Number(mm[5]),
      Number(mm[6]),
      Number.isFinite(msPart) ? msPart : 0
    ).getTime();
    return Number.isFinite(tLocal) ? tLocal : 0;
  }
  const ts = new Date(s).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

/**
 * Timestamp en ms para ordenar; inválidos → 0.
 * @param {object|null|undefined} m
 * @returns {number}
 */
export function getMessageSortTimestamp(m) {
  return parseMessageSortDateToMs(pickMessageSortDateRaw(m));
}

/** @deprecated usar getMessageSortTimestamp */
export function messageSortTimestampMs(m) {
  return getMessageSortTimestamp(m);
}

function isTempSortMessage(m) {
  const id = m?.id ?? m?.mensaje_id ?? m?.message_id ?? m?.__id;
  const sid = String(id ?? '').trim();
  return /^temp[_-]/i.test(sid);
}

/**
 * Id numérico para desempate (mensajes con misma fecha_envio).
 * temp_* → sufijo numérico si existe.
 * @param {object|null|undefined} m
 * @returns {number}
 */
export function getMessageNumericId(m) {
  const id = m?.id ?? m?.mensaje_id ?? m?.message_id ?? m?.__id;
  if (id == null || id === '') return 0;
  const s = String(id).trim();
  if (/^temp[_-]/i.test(s)) {
    const n = Number(s.replace(/^temp[_-]?/i, ''));
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(id);
  return Number.isFinite(n) ? n : 0;
}

/** @deprecated usar getMessageNumericId */
export function messageNumericIdForSort(m) {
  return getMessageNumericId(m);
}

/**
 * Comparador estable: más viejos primero; mismo instante → reales antes que optimistas temp_*; luego id ascendente.
 * @param {object|null|undefined} a
 * @param {object|null|undefined} b
 * @returns {number}
 */
export function compareMessagesChronological(a, b) {
  const rawTa = getMessageSortTimestamp(a);
  const rawTb = getMessageSortTimestamp(b);
  /**
   * Sin fecha válida (0): no mezclar al inicio del hilo con mensajes antiguos bien fechados
   * (provoca “último mensaje en el medio”). Se agrupan al final y se desempata por id ASC / temp.
   */
  const ta = rawTa === 0 ? Number.MAX_SAFE_INTEGER : rawTa;
  const tb = rawTb === 0 ? Number.MAX_SAFE_INTEGER : rawTb;
  if (ta !== tb) return ta - tb;
  const tierA = isTempSortMessage(a) ? 1 : 0;
  const tierB = isTempSortMessage(b) ? 1 : 0;
  if (tierA !== tierB) return tierA - tierB;
  const ia = getMessageNumericId(a);
  const ib = getMessageNumericId(b);
  if (ia !== ib) return ia - ib;
  const sa = String(a?.__id ?? a?.id ?? a?.mensaje_id ?? '');
  const sb = String(b?.__id ?? b?.id ?? b?.mensaje_id ?? '');
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
}

/**
 * Copia ordenada cronológicamente (chat: arriba viejos, abajo nuevos).
 * @param {object[]} arr
 * @returns {object[]}
 */
export function sortMessagesChronological(arr) {
  if (!Array.isArray(arr)) return [];
  if (arr.length < 2) return [...arr];
  return [...arr].sort(compareMessagesChronological);
}

/**
 * Fusiona historial y realtime por id, ordenados por fecha
 * @param {Array} history - Mensajes del API (orden ASC)
 * @param {Array} realtime - Mensajes llegados por WS
 * @returns {Array} Lista única ordenada por fecha_envio ASC, id ASC si empatan
 */
export function reconcileMessages(history = [], realtime = []) {
  const byId = new Map();
  const toMillis = (value) => {
    if (value == null || value === '') return 0;
    return parseMessageSortDateToMs(String(value).trim());
  };
  function tryStrMsg(v) {
    if (v == null) return null;
    const s = typeof v === 'string' ? v.trim() : String(v).trim();
    return s !== '' ? s : null;
  }
  const getEditTs = (m) => {
    return Math.max(
      toMillis(m?.fecha_edicion),
      toMillis(m?.updated_at),
      toMillis(m?.raw?.fecha_edicion),
      toMillis(m?.raw?.updated_at)
    );
  };
  const getVisibleText = (m) => {
    const candidates = [m?.display_text, m?.text, m?.mensaje, m?.contenido, m?.translated_text];
    for (const c of candidates) {
      const t = String(c ?? '').trim();
      if (t !== '') return t;
    }
    return '';
  };
  /** read/leido > delivered > sent — evita que un GET con "delivered" pise "read" del socket. */
  const deliveryRank = (msg) => {
    if (!msg || typeof msg !== 'object') return 0;
    const v = String(msg.delivery_status ?? msg.estado_lectura ?? msg.status ?? '').toLowerCase();
    if (v === 'read' || v === 'leido') return 3;
    if (v === 'delivered') return 2;
    if (v === 'sent') return 1;
    return 0;
  };
  const pickDeliveryFieldsFromHigherRank = (a, b) => {
    const ra = deliveryRank(a);
    const rb = deliveryRank(b);
    const win = ra >= rb ? a : b;
    return {
      delivery_status: win?.delivery_status,
      estado_lectura: win?.estado_lectura,
      status: win?.status,
      read_at: win?.read_at
    };
  };
  const preserveEntrance = (current, merged) => {
    if (current && current.__entradaAnim === true) {
      merged.__entradaAnim = true;
      if (merged.raw && typeof merged.raw === 'object') {
        merged.raw = { ...merged.raw, __entradaAnim: true };
      }
    }
    return merged;
  };
  /** Una sola clave string por mensaje (evita duplicados 123 vs "123" en el Map). */
  const messageKey = (m) => {
    const id = m?.id ?? m?.mensaje_id ?? m?.message_id ?? m?.__id;
    if (id == null || id === '') return null;
    const s = String(id).trim();
    if (s === '' || s === 'undefined' || s === 'null') return null;
    return s;
  };
  const put = (m) => {
    const key = messageKey(m);
    if (key == null) return;
    const sortRaw = pickMessageSortDateRaw(m);
    const feStored =
      tryStrMsg(m?.fecha_envio) ??
      tryStrMsg(m?.created_at) ??
      sortRaw ??
      '';
    byId.set(key, { ...m, fecha_envio: feStored || m.fecha_envio });
  };
  const hasMediaBlob = (x) => x?.archivo_data != null && String(x.archivo_data).trim() !== '';
  const hasThumb = (x) => x?.archivo_thumbnail != null && String(x.archivo_thumbnail).trim() !== '';

  /** Avisos __ARCHAT_I18N_V1__: el WS puede dejar solo texto localizado; el GET conserva el blob en columnas legacy. */
  const hasArchatI18nBlob = (x) => {
    if (!x || typeof x !== 'object') return false;
    const parts = [
      x.contenido_original,
      x.mensaje,
      x.text,
      x.display_text,
      x.raw?.contenido_original,
      x.raw?.mensaje,
      x.raw?.text,
    ];
    return parts.some((s) => isArchatI18nV1BlobString(s));
  };

  const mergeHistory = (m) => {
    const key = messageKey(m);
    if (key == null) return;
    if (!byId.has(key)) {
      put(m);
      return;
    }
    const current = byId.get(key);
    const historyEditTs = getEditTs(m);
    const currentEditTs = getEditTs(current);
    const historyText = getVisibleText(m);
    const currentText = getVisibleText(current);
    const isMarkerMsg =
      (typeof m?.mensaje === 'string' && (m.mensaje.includes('REVERSE_EN_TO_ES') || m.mensaje.includes('FORWARD_ES_TO_EN') || m.mensaje.includes('REVERSE_EN_TO_ES'))) ||
      (typeof m?.text === 'string' && (m.text.includes('REVERSE_EN_TO_ES') || m.text.includes('FORWARD_ES_TO_EN') || m.text.includes('REVERSE_EN_TO_ES'))) ||
      (typeof current?.mensaje === 'string' && (current.mensaje.includes('REVERSE_EN_TO_ES') || current.mensaje.includes('FORWARD_ES_TO_EN') || current.mensaje.includes('REVERSE_EN_TO_ES'))) ||
      (typeof current?.text === 'string' && (current.text.includes('REVERSE_EN_TO_ES') || current.text.includes('FORWARD_ES_TO_EN') || current.text.includes('REVERSE_EN_TO_ES')));
    if (isMarkerMsg) {
      console.log('[AUTH_DEBUG][reconcileMessages_mergeHistory]', JSON.stringify({
        key, message_id: m?.id ?? m?.mensaje_id,
        historyText: String(historyText).slice(0, 200),
        currentText: String(currentText).slice(0, 200),
        historyEditTs, currentEditTs,
        history_delivery_view: m?.delivery_view ?? m?.raw?.delivery_view,
        current_delivery_view: current?.delivery_view ?? current?.raw?.delivery_view,
        history_raw_dv: m?.raw?.delivery_view,
        current_raw_dv: current?.raw?.delivery_view,
        history_text: String(m?.text ?? '').slice(0, 200),
        history_display_text: String(m?.display_text ?? '').slice(0, 200),
        history_mensaje: String(m?.mensaje ?? '').slice(0, 200),
        current_text: String(current?.text ?? '').slice(0, 200),
        current_display_text: String(current?.display_text ?? '').slice(0, 200),
        current_mensaje: String(current?.mensaje ?? '').slice(0, 200),
        action: historyEditTs > currentEditTs ? 'HISTORY_WINS_EDIT_TS' :
          (historyEditTs === currentEditTs && historyText !== '' && historyText !== currentText) ? 'HISTORY_WINS_TEXT_DIFF' : 'CURRENT_KEEPS',
      }));
    }
    // Si historial trae una edición más nueva, debe prevalecer sobre realtime stale.
    if (
      historyEditTs > currentEditTs ||
      (historyEditTs === currentEditTs && historyText !== '' && historyText !== currentText)
    ) {
      const merged = {
        ...current,
        ...m,
        fecha_envio:
          tryStrMsg(m.fecha_envio) ??
          tryStrMsg(m.created_at) ??
          tryStrMsg(current?.fecha_envio) ??
          pickMessageSortDateRaw(m) ??
          current?.fecha_envio,
      };
      preserveEntrance(current, merged);
      const d = pickDeliveryFieldsFromHigherRank(current, merged);
      Object.entries(d).forEach(([k, v]) => {
        if (v !== undefined) merged[k] = v;
      });
      byId.set(key, merged);
      const isMarkerAfterMerge =
        (typeof merged?.mensaje === 'string' && merged.mensaje.includes('REVERSE_EN_TO_ES')) ||
        (typeof merged?.text === 'string' && merged.text.includes('REVERSE_EN_TO_ES'));
      if (isMarkerAfterMerge) {
        const finalM = byId.get(key);
        console.log('[AUTH_DEBUG][reconcileMessages_AFTER_HISTORY_WINS]', JSON.stringify({
          key, message_id: merged?.id ?? merged?.mensaje_id,
          final_text: String(finalM?.text ?? '').slice(0, 200),
          final_display_text: String(finalM?.display_text ?? '').slice(0, 200),
          final_mensaje: String(finalM?.mensaje ?? '').slice(0, 200),
          final_raw_dv: finalM?.raw?.delivery_view,
          final_delivery_view: finalM?.delivery_view,
        }));
      }
      return;
    }
    const needArchatBlob = hasArchatI18nBlob(m) && !hasArchatI18nBlob(current);
    if (needArchatBlob) {
      const merged = {
        ...current,
        ...m,
        fecha_envio:
          tryStrMsg(m.fecha_envio) ??
          tryStrMsg(m.created_at) ??
          tryStrMsg(current?.fecha_envio) ??
          pickMessageSortDateRaw(m) ??
          current?.fecha_envio,
        contenido_original: m.contenido_original ?? current.contenido_original,
        mensaje: m.mensaje ?? m.contenido_original ?? current.mensaje,
        contenido: m.contenido ?? m.mensaje ?? current.contenido,
        text: m.text ?? m.mensaje ?? current.text,
        display_text: m.display_text ?? m.text ?? m.mensaje ?? current.display_text,
        tipo: m.tipo ?? current.tipo,
        translated_text: m.translated_text ?? current.translated_text,
        original_text: m.original_text ?? current.original_text,
      };
      if (m.raw && typeof m.raw === 'object') {
        merged.raw = {
          ...(current.raw && typeof current.raw === 'object' ? current.raw : {}),
          ...m.raw,
        };
      }
      preserveEntrance(current, merged);
      const d = pickDeliveryFieldsFromHigherRank(current, merged);
      Object.entries(d).forEach(([k, v]) => {
        if (v !== undefined) merged[k] = v;
      });
      byId.set(key, merged);
      return;
    }
    // Adjuntos: /notify-message (WS) no envía archivo_data; el GET de historial sí (base64 vía API).
    const needBlob = hasMediaBlob(m) && !hasMediaBlob(current);
    const needThumb = hasThumb(m) && !hasThumb(current);
    if (needBlob || needThumb) {
      const merged = {
        ...current,
        ...m,
        fecha_envio:
          tryStrMsg(m.fecha_envio) ??
          tryStrMsg(m.created_at) ??
          tryStrMsg(current?.fecha_envio) ??
          pickMessageSortDateRaw(m) ??
          current?.fecha_envio,
        archivo_data: m.archivo_data != null ? m.archivo_data : current.archivo_data,
        archivo_thumbnail: m.archivo_thumbnail != null ? m.archivo_thumbnail : current.archivo_thumbnail,
        archivo_nombre_original: m.archivo_nombre_original ?? current.archivo_nombre_original,
        archivo_mime_type: m.archivo_mime_type ?? current.archivo_mime_type,
        archivo_tamano: m.archivo_tamano ?? current.archivo_tamano,
        archivo_ancho: m.archivo_ancho ?? current.archivo_ancho,
        archivo_alto: m.archivo_alto ?? current.archivo_alto,
        archivo_duracion: m.archivo_duracion ?? current.archivo_duracion,
        tipo: m.tipo ?? current.tipo,
      };
      if (m.raw && typeof m.raw === 'object') {
        merged.raw = {
          ...(current.raw && typeof current.raw === 'object' ? current.raw : {}),
          ...m.raw,
        };
      }
      preserveEntrance(current, merged);
      const d = pickDeliveryFieldsFromHigherRank(current, merged);
      Object.entries(d).forEach(([k, v]) => {
        if (v !== undefined) merged[k] = v;
      });
      byId.set(key, merged);
    }
    // delivery_view: history (GET) carries per-user frozen snapshot; WS never does.
    // Merge it into current to preserve MessageItem.jsx frozen text logic.
    const hasHistoryDv = m?.raw?.delivery_view && typeof m.raw.delivery_view === 'object';
    const hasCurrentDv = current?.raw?.delivery_view && typeof current.raw.delivery_view === 'object';
    if (hasHistoryDv && !hasCurrentDv) {
      const isMarkerMsg =
        (typeof m?.mensaje === 'string' && (m.mensaje.includes('REVERSE_EN_TO_ES') || m.mensaje.includes('FORWARD_ES_TO_EN') || m.mensaje.includes('REVERSE_EN_TO_ES'))) ||
        (typeof m?.text === 'string' && (m.text.includes('REVERSE_EN_TO_ES') || m.text.includes('FORWARD_ES_TO_EN') || m.text.includes('REVERSE_EN_TO_ES'))) ||
        (typeof current?.mensaje === 'string' && (current.mensaje.includes('REVERSE_EN_TO_ES') || current.mensaje.includes('FORWARD_ES_TO_EN') || current.mensaje.includes('REVERSE_EN_TO_ES'))) ||
        (typeof current?.text === 'string' && (current.text.includes('REVERSE_EN_TO_ES') || current.text.includes('FORWARD_ES_TO_EN') || current.text.includes('REVERSE_EN_TO_ES')));
      if (isMarkerMsg) {
        console.log('[AUTH_DEBUG][reconcileMessages_DV_MERGE]', JSON.stringify({
          key,
          message_id: m?.id ?? m?.mensaje_id,
          action: 'MERGING_DV_INTO_CURRENT',
          history_raw_dv_display_text: (m?.raw?.delivery_view?.display_text || '').slice(0, 200),
          current_text: String(current?.text ?? '').slice(0, 200),
          current_raw_dv: current?.raw?.delivery_view,
        }));
      }
      const merged = {
        ...current,
        raw: {
          ...(current?.raw && typeof current.raw === 'object' ? current.raw : {}),
          delivery_view: m.raw.delivery_view,
        },
      };
      preserveEntrance(current, merged);
      byId.set(key, merged);
      if (isMarkerMsg) {
        const finalM = byId.get(key);
        console.log('[AUTH_DEBUG][reconcileMessages_DV_AFTER_MERGE]', JSON.stringify({
          key, message_id: m?.id ?? m?.mensaje_id,
          final_text: String(finalM?.text ?? '').slice(0, 200),
          final_display_text: String(finalM?.display_text ?? '').slice(0, 200),
          final_mensaje: String(finalM?.mensaje ?? '').slice(0, 200),
          final_raw_dv: finalM?.raw?.delivery_view,
          final_raw_dv_dt: (finalM?.raw?.delivery_view?.display_text || '').slice(0, 200),
        }));
      }
    }
  };
  // Realtime primero: el WS ya trae texto para el receptor (traducido); el GET puede pisar con original si comparamos por fecha.
  realtime.forEach(put);
  history.forEach(mergeHistory);
  const combined = Array.from(byId.values());
  combined.sort(compareMessagesChronological);
  const markerMsg = combined.find(m =>
    (typeof m?.mensaje === 'string' && (m.mensaje.includes('REVERSE_EN_TO_ES') || m.mensaje.includes('FORWARD_ES_TO_EN') || m.mensaje.includes('REVERSE_EN_TO_ES'))) ||
    (typeof m?.text === 'string' && (m.text.includes('REVERSE_EN_TO_ES') || m.text.includes('FORWARD_ES_TO_EN') || m.text.includes('REVERSE_EN_TO_ES')))
  );
  if (markerMsg) {
    console.log('[AUTH_DEBUG][reconcileMessages_FINAL]', JSON.stringify({
      message_id: markerMsg?.id ?? markerMsg?.mensaje_id,
      final_text: String(markerMsg?.text ?? '').slice(0, 200),
      final_display_text: String(markerMsg?.display_text ?? '').slice(0, 200),
      final_mensaje: String(markerMsg?.mensaje ?? '').slice(0, 200),
      final_delivered_display_text: (markerMsg?.delivered_display_text || '').slice(0, 200),
      final_delivery_view: markerMsg?.delivery_view,
      final_raw_dv: markerMsg?.raw?.delivery_view,
      final_raw_dv_dt: (markerMsg?.raw?.delivery_view?.display_text || '').slice(0, 200),
      total_messages: combined.length,
    }));
  }
  return combined;
}
